<template>
  <div>
    <button
      :style="{ top: btnPos.top + 'px', left: btnPos.left + 'px' }"
      type="button"
      :class="[`ai-report-btn ${!noData ? animationClass : ''}`, {disabled: noData}]"
      ref="aiReport"
      @animationend="onAnimationEnd">
      <span class="blur"></span>
      <i class="icon icon--ai-badge" :class="{disabled: noData}"></i>
      <span class="title">AI Report</span>
      <template v-if="noData">
        <span class="report-no-data">NO DATA</span>
      </template>
      <template v-else>
        <span class="desc">{{ $t('reporting.scroll.aiReport') }}</span>
      </template>
    </button>

    <dimmed v-if="isShowReportPayment">
      <ai-report-payment
        @getSegment="val => this.createEndData = { endDate: val.endDate, endTime: val.endTime }"
        :report-info="applyData"
        :report-name="$t('reporting.scroll.reportTitle')"
        report-type="segment"
        :apiParamsData="getParams()"
        @checkDuplicate="checkDuplicate"
        @payment="requestPayment"
        @close="isShowReportPayment = false"
        @onChargeJam="onChargeJam"
      />
    </dimmed>

    <dimmed v-if="isChargingJam">
      <charge-pv
        :topUpJam="isChargingJam"
        :userData="userData"
        :planData="planData"
        :remainJam="remainJam"
        @closeLayer="isChargingJam = false" />
    </dimmed>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import { createCXReport, getAIReportData } from '@/api/hub'
import { makeDraggable } from '@/libs/draggable'
import Dimmed from '@/components/ui/Dimmed.vue'
import AiReportPayment from '@/components/reporting-heatmaps/AiReportPayment.vue'
import { checkReportDuplicate, editReportTitle } from '@/api/ai'
import { saveReportPayment } from '@/api/jam'
import AppDialog from '@/components/ui/AppDialog.vue'
import { create } from 'vue-modal-dialogs'
import ChargePv from '@/components/user-info/manage-plan/ChargePv.vue'
import { format } from 'date-fns'

const aiReportOpenDialog = create(AppDialog, 'dialogType')

export default {
  name: 'AiReportFloatingButton',
  components: {
    ChargePv,
    AiReportPayment,
    Dimmed
  },
  props: {
    apiParamsData: {
      type: Object,
      default: null
    },
    metric: {
      type: Object,
      default: null
    },
    freqPv: {
      type: Array,
      default: () => []
    },
    isFrequency: {
      type: Boolean,
      default: false
    },
    segmentsDataForCapture: {
      type: [Array, Object, String],
      default: null
    },
    noData: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      selectedReportInfo: null,
      isShowReportPayment: false,
      cxReportData: null,
      cxReportId: null,
      animationClass: 'ld-bounce-in',
      draggableInit: false,
      reportPaymentType: null,
      reportPaymentId: null,
      isChargingJam: false,
      remainJam: true,
      createEndData: '',
      btnPos: { top: 0, left: 0 }
    }
  },
  computed: {
    ...mapState([
      'userData',
      'planData',
      'applyData'
    ]),
    currentLang () {
      return this.$i18n.locale
    }
  },
  mounted () {
    this.onStartAIReportButton()
  },
  methods: {
    ...mapMutations([
      'setCxReportId'
    ]),
    onStartAIReportButton () {
      if (this.draggableInit) return
      const btnEl = this.$refs.aiReport
      if (!btnEl) return

      this.draggableInit = true
      this.$nextTick(() => {
        const targetEl = document.querySelector('.reporting__scroll') || document.querySelector('.reporting-heatmap-analysis')
        const rect = targetEl ? targetEl.getBoundingClientRect() : null
        const defaultLeft = window.innerWidth - btnEl.offsetWidth - 16
        const defaultTop = window.innerHeight - btnEl.offsetHeight - 16

        this.btnPos = {
          top: rect ? Math.max(0, Math.min(rect.top + 30, window.innerHeight - btnEl.offsetHeight)) : defaultTop,
          left: rect ? Math.max(0, Math.min(rect.left - 42, window.innerWidth - btnEl.offsetWidth)) : defaultLeft
        }

        makeDraggable(btnEl, {
          onClick: () => {
            if (!this.noData) {
              this.isShowReportPayment = true
            }
          }
        })
      })
    },
    checkDuplicate (lang, creditReport = null) {
      const requestData = this.getParams()
      if (!requestData) {
        aiReportOpenDialog('report.reportRequest.sysError')
        return
      }

      requestData.lang = lang
      requestData.checkType = 'segment'
      checkReportDuplicate(requestData, true).then(res => {
        if (res) {
          this.isShowReportPayment = false
          aiReportOpenDialog('report.reportRequest.duplicate').then((response) => {
            if (response) {
              window.open('/mine', '_blank')
            }
          })
        } else if (creditReport) {
          this.requestPayment(creditReport)
          this.isShowReportPayment = false
        }
      })
    },
    requestPayment (payload) {
      this.selectedReportInfo = {
        title: payload.title,
        reportData: payload.reportData,
        lang: payload.lang
      }

      const params = {
        variantKey: payload.paymentType,
        serviceType: 'beusable',
        price: payload.paymentType === 'jam' ? payload.price : payload.price * 1.1
      }

      saveReportPayment(params, true)
        .then(resp => {
          if (resp) {
            this.reportPaymentType = payload.paymentType
            this.reportPaymentId = resp._id
            this.onCreatReport(payload.title)
          } else {
            this.resetReportState()
          }
        }).catch(err => {
          console.log(err)
          this.resetReportState()
        })
    },
    resetReportState () {
      this.variantKey = null
      this.reportPaymentType = null
      this.reportPaymentId = null
    },
    onChargeJam (remainJam) {
      this.isShowReportPayment = false
      this.remainJam = remainJam
      this.isChargingJam = true
    },
    onAnimationEnd (e) {
      if (e.animationName === 'ld-bounce-in') {
        this.animationClass = 'ld-bounce-in2 infinite-bounce'
      }
    },
    async onCreatReport (title = '') {
      this.$emit('onReportLoading', true)
      const params = {
        id: this.cxReportId,
        paid: true,
        paymentId: this.reportPaymentId,
        lang: this.selectedReportInfo.lang
      }
      try {
        if (!this.cxReportData) {
          await this.onLoadScrollDataForAI()
        }

        await editReportTitle({
          reportId: this.cxReportId,
          title: title
        })

        await createCXReport({ ...params, id: this.cxReportId, endTime: this.createEndData.endTime, segmentsDataForCapture: this.segmentsDataForCapture })

        this.cxReportData = null

        aiReportOpenDialog('report.reportRequest.success').then((response) => {
          if (response) {
            window.open('/mine', '_blank')
          }
        })
      } catch (err) {
        console.error('getCXReport/createCXReport : ', err)
        aiReportOpenDialog('report.reportRequest.sysError')
      } finally {
        this.$emit('onReportLoading', false)
        this.resetReportState()
      }
    },
    getParams () {
      if (!this.apiParamsData) {
        return null
      }

      const params = {
        ...this.apiParamsData,
        event: 'scroll',
        lang: this.currentLang
      }

      if (this.freqPv && this.isFrequency) {
        params.freq = this.freqPv
      } else if (JSON.parse(localStorage.getItem('paramData'))) {
        params.freq = JSON.parse(localStorage.getItem('paramData')).freqPv
      }

      if (this.apiParamsData.endDate === format(new Date(), 'YYYY-MM-DD')) {
        params.endTime = format(new Date(), 'HH:mm:ss')
      }

      return params
    },
    async onLoadScrollDataForAI () {
      const params = this.getParams()
      if (!params || !this.metric || !this.metric.pv) {
        throw new Error('Missing params for AI report')
      }

      params.pv = this.metric.pv

      const res = await getAIReportData(params)
      this.cxReportId = res.result.aiReportId
      this.cxReportData = res.result.data
      this.setCxReportId(this.cxReportId)
    }
  }
}
</script>

<style lang="scss">
.ai-report-btn {
  width: 132px;
  height: 132px;
  border-radius: 14px;
  background: linear-gradient(94deg, #0085FF 0%, #0058DD 100%);
  box-shadow: 0 38px 38px -10px rgba(0, 0, 0, 0.44);
  transition: scale 0.5s ease-in-out;
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  padding: 14px 12px;
  gap: 5px;

  &.ld-bounce-in {
    animation: ld-bounce-in 1s 1 linear;
  }

  &:not(:hover) {
    &.infinite-bounce {
      animation: ld-bounce-in2 5s linear infinite;
    }
  }

  &:after {
    content: '';
    position: absolute;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 80px;
    background: url(../../assets/img/heatmap/chatbot-normal.svg?url) no-repeat center / contain;
  }

  &:before {
    content: '';
    width: 52px;
    height: 4px;
    border-radius: 50em;
    background-color: #fff;
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0;
    transition: opacity 0.5s ease-in-out;
  }

  &:hover {
    scale: 1.1;

    &:after {
      background-image: url(../../assets/img/heatmap/chatbot-hover.svg?url);
    }

    &:before {
      opacity: 1;
    }

    .blur {
      opacity: 1;
    }
  }

  &.disabled {
    background: linear-gradient(94deg, #A8D1FB 0%, #A2C1F2 100%);
    box-shadow: 0 38px 38px -10px rgba(0, 0, 0, 0.12);
    cursor: default;
    scale: 1;
    padding-bottom: 8px;

    &:after {
      background-image: url(../../assets/img/heatmap/chatbot-disabled.svg);
    }

    &:hover {
      scale: 1;
    }

    .blur {
      background-color: #E9F7FF;
      opacity: 0.6;
      filter: blur(24px);
    }

  }

  .blur {
    transition: opacity 0.3s ease-in-out;
    opacity: 0;
    width: 90px;
    height: 90px;
    background-color: #4CB1FF;
    filter: blur(24px);
    position: absolute;
    border-radius: 50em;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    z-index: -1;
  }

  .title {
    color: #FFF;
    font-size: 16px;
    font-weight: 600;
    line-height: 1;
  }

  .desc {
    color: #BADFFF;
    font-size: 12px;
    font-weight: 500;
    line-height: 1;
  }

  .report-no-data {
    border-radius: 999px;
    background: #FFF;
    display: flex;
    width: 116px;
    height: 20px;
    padding: 0 33px;
    justify-content: center;
    align-items: center;
    color: #777;
    font-size: 11px;
    font-weight: 600;
    line-height: 100%; /* 11px */
    box-sizing: border-box;
  }

  .icon--ai-badge {
    position: absolute;
    top: 4px;
    left: 4px;
  }
}

@keyframes ld-bounce-in {
  0% {
    animation-timing-function: cubic-bezier(0.0383, 0.3388, 0.0421, 0.6652);
    transform: matrix(0.01, 0, 0, 0.01, 0, 0);
    opacity: 0;
  }

  4.7% {
    animation-timing-function: cubic-bezier(0.3296, 0.3336, 0.5772, 0.6672);
    transform: matrix(0.556, 0, 0, 0.556, 0, 0);
    opacity: 0.1;
  }

  8.4% {
    animation-timing-function: cubic-bezier(0.0781, 0.1617, 0.0874, 0.9301);
    transform: matrix(1.002, 0, 0, 1.002, 0, 0);
    opacity: 0.2;
  }

  14.11% {
    animation-timing-function: cubic-bezier(0.3071, 0.3115, 0.5563, 1.0037);
    transform: matrix(0.936, 0, 0, 0.936, 0, 0);
    opacity: 0.39;
  }

  17.82% {
    animation-timing-function: cubic-bezier(0.1259, 0.2548, 0.1241, 0.9968);
    transform: matrix(1, 0, 0, 1, 0, 0);
    opacity: 0.53;
  }

  24.32% {
    animation-timing-function: cubic-bezier(0.2598, 0.3517, 0.4995, 1.2445);
    transform: matrix(0.987, 0, 0, 0.987, 0, 0);
    opacity: 0.74;
  }

  25.23% {
    animation-timing-function: cubic-bezier(0.2265, 0.3321, 0.3804, 1.1878);
    transform: matrix(0.989, 0, 0, 0.989, 0, 0);
    opacity: 0.77;
  }

  32.43% {
    animation-timing-function: cubic-bezier(0.1648, 0.5132, 0.2623, 1.0033);
    transform: matrix(1, 0, 0, 1, 0, 0);
    opacity: 1;
  }

  100% {
    transform: matrix(1, 0, 0, 1, 0, 0);
    opacity: 1;
  }
}

@keyframes ld-bounce-in2 {
  0%, 100% {
    transform: matrix(1, 0, 0, 1, 0, 0);
  }
  3.7% {
    transform: matrix(1.07, 0, 0, 1.07, 0, 0);
  }
  7.4% {
    transform: matrix(0.98, 0, 0, 0.98, 0, 0);
  }
  11.1% {
    transform: matrix(1.02, 0, 0, 1.02, 0, 0);
  }
  14.8% {
    transform: matrix(0.99, 0, 0, 0.99, 0, 0);
  }
  18.5% {
    transform: matrix(1, 0, 0, 1, 0, 0);
  }
}
</style>
